import React, { useState } from 'react';
import { ClipboardList, FileText, Package, Tags, LogOut, Menu, X } from 'lucide-react';

export type AdminTab = 'orders' | 'quotes' | 'products' | 'categories';

const NAV_ITEMS: { key: AdminTab; label: string; icon: React.ElementType }[] = [
  { key: 'orders', label: 'Commandes', icon: ClipboardList },
  { key: 'quotes', label: 'Devis', icon: FileText },
  { key: 'products', label: 'Produits', icon: Package },
  { key: 'categories', label: 'Catégories', icon: Tags },
];

interface Props {
  tab: AdminTab;
  onTabChange: (tab: AdminTab) => void;
  onLogout: () => void;
  counts: Partial<Record<AdminTab, number>>;
  title: string;
  children: React.ReactNode;
}

const AdminLayout: React.FC<Props> = ({ tab, onTabChange, onLogout, counts, title, children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);

  const navContent = (
    <>
      <div className="flex items-center gap-2.5 px-5 py-5">
        <img
          src="/assets/Logo_EAM.png"
          alt=""
          className="h-8 w-8 object-contain"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
        <div className="min-w-0">
          <p className="truncate font-display text-sm font-bold text-coffee-200">Eudora Aromas</p>
          <p className="text-[11px] text-coffee-100">Espace admin</p>
        </div>
      </div>

      <p className="px-5 pb-2 pt-3 text-[11px] font-semibold uppercase tracking-wider text-coffee-100/70">Menu</p>
      <nav className="flex flex-1 flex-col gap-1 px-3">
        {NAV_ITEMS.map(({ key, label, icon: Icon }) => {
          const active = tab === key;
          const count = counts[key];
          return (
            <button
              key={key}
              type="button"
              onClick={() => { onTabChange(key); setMobileOpen(false); }}
              className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                active ? 'bg-forest-200/12 text-forest-200' : 'text-coffee-200/80 hover:bg-cream-100 hover:text-coffee-200'
              }`}
            >
              <Icon className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />
              <span className="flex-1 text-left">{label}</span>
              {typeof count === 'number' && (
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${active ? 'bg-forest-200/20 text-forest-200' : 'bg-cream-200 text-coffee-100'}`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      <div className="p-3">
        <button
          type="button"
          onClick={onLogout}
          className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-coffee-100 transition-colors hover:bg-red-50 hover:text-red-600"
        >
          <LogOut className="h-[18px] w-[18px]" aria-hidden="true" />
          Déconnexion
        </button>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen bg-cream-100">
      {/* Desktop sidebar */}
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r border-cream-200/70 bg-white lg:flex">
        {navContent}
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-coffee-200/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 flex h-full w-72 flex-col bg-white shadow-2xl">
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              className="absolute right-3 top-4 flex h-8 w-8 items-center justify-center rounded-full text-coffee-100 hover:bg-cream-100"
              aria-label="Fermer le menu"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
            {navContent}
          </aside>
        </div>
      )}

      {/* Main column */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-cream-200/70 bg-white/90 px-4 py-4 backdrop-blur-sm sm:px-6">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-cream-200 text-coffee-200 lg:hidden"
            aria-label="Ouvrir le menu"
          >
            <Menu className="h-4.5 w-4.5" aria-hidden="true" />
          </button>
          <h1 className="font-display text-lg font-bold text-coffee-200 sm:text-xl">{title}</h1>
        </header>

        <main className="flex-1 px-4 py-6 sm:px-6 lg:px-8">{children}</main>
      </div>
    </div>
  );
};

export default AdminLayout;
